/* eslint-disable jsx-a11y/no-autofocus */
import React, { createContext, useContext, useEffect } from "react";
import { Center, Modal, ModalOverlay, useDisclosure, CircularProgress } from "@chakra-ui/react";
import theme from "theme";

const OverlaySpinnerContext = createContext();

/**
 * OverlaySpinnerProvider
 * @param {object} props
 * @param {JSX.Element} props.children
 * @description
 * This provider handles the showing and hiding of a full page overlay with a
 * spinner inside of it. This makes it so that any flow can open the spinner
 * on top of whatever is being displayed below.
 */
export const OverlaySpinnerProvider = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  // On mount, if the spinner is open, close it. This should only happen when
  // we're developing and react's fast refresh was triggered.
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OverlaySpinnerContext.Provider
      value={{ showOverlaySpinner: onOpen, hideOverlaySpinner: onClose, isOpen }}
    >
      <>
        {children}

        <Modal
          isOpen={isOpen}
          onClose={onClose}
          closeOnOverlayClick={false}
          closeOnEsc={false}
          autoFocus
          blockScrollOnMount
          lockFocusAcrossFrames
        >
          <ModalOverlay data-testid="full-overlay-spinner" />
          <Center
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={theme.zIndices.modal + 1}
            // onClick={onClose} // uncomment this if we want a click on the modal to close itself out.  Not sure currently if that's what we want, but leaving here as an option
          >
            <CircularProgress isIndeterminate color="integrator.500" />
          </Center>
        </Modal>
      </>
    </OverlaySpinnerContext.Provider>
  );
};

export const useOverlaySpinner = () => useContext(OverlaySpinnerContext);
